import type { LucideIcon } from "lucide-react";

import { ColocaKpiCard, type ColocaKpiCardProps } from "@/shared/components/data-display/ColocaKpiCard";

/** Derived from the DS rather than redeclared, so a change there is a compile error and not a render bug. */
export type AccessKpiAccent = NonNullable<ColocaKpiCardProps["accent"]>;

export interface AccessKpiTile<K extends string> {
  key: K;
  label: string;
  /** `null` renders `—`: the figure is UNKNOWN, which a `0` would state as a fact. */
  value: number | null;
  /** Shown under the value. Callers pass it only when it says something — see the strips. */
  meta?: string;
  icon: LucideIcon;
  accent: AccessKpiAccent;
}

export interface AccessKpiStripProps<K extends string> {
  ariaLabel: string;
  /** Exactly four; the grid is `lg:grid-cols-4`, and the three catalogs each have four buckets. */
  tiles: readonly AccessKpiTile<K>[];
  value: K;
  onChange: (next: K) => void;
}

/**
 * The catalog summary AND its bucket filter, in one row of tiles — not a stat strip above a chip row, so each
 * number lives in exactly one place and cannot disagree with itself.
 *
 * **A tile never invents a zero.** While the catalog is loading, and for any figure derived from a source
 * that could not be read, the value is `null` and renders `—`. A `0` on "Sin usar" would assert that every
 * policy is in use — the opposite of "we could not ask", and the two lead to opposite actions. The tiles stay
 * selectable regardless: the filter works without the figure.
 */
export function AccessKpiStrip<K extends string>({
  ariaLabel,
  tiles,
  value,
  onChange,
}: AccessKpiStripProps<K>) {
  return (
    <div role="group" aria-label={ariaLabel} className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {tiles.map((tile) => (
        <ColocaKpiCard
          key={tile.key}
          label={tile.label}
          value={tile.value === null ? "—" : String(tile.value)}
          icon={tile.icon}
          accent={tile.accent}
          iconPlacement="end"
          selected={value === tile.key}
          onSelect={() => onChange(tile.key)}
          meta={tile.meta}
        />
      ))}
    </div>
  );
}
